import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

// ── Collapsible Agent Panel ──
export default function AgentPanel({ usgsData, demoEnabled = true }) {
  const { C } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedAgents, setExpandedAgents] = useState(true);
  const agents = [
    { label: 'QA/QC Agent', color: '#fbbf24', status: 'Active' },
    { label: 'GIS Design Agent', color: '#a78bfa', status: 'Active' },
    { label: 'Anomaly Agent', color: '#f87171', status: 'Active' },
    { label: 'Support Agent', color: '#00d4ff', status: 'Standby' },
  ];

  // Agent messages — contextual to USGS Cape Cod Bay survey
  // Hard-coded Cape Cod demo content: only shown for the demo (no project / Cape Cod project).
  const agentMsgs = !demoEnabled ? [] : [
    { id: 1, agent: 'GIS Design Agent', msg: `Loaded ${Object.values(usgsData || {}).reduce((s, g) => s + (g?.features?.length || 0), 0)} features from USGS Cape Cod Bay dataset. 1,097 tracklines, 49 sediment samples, 439 SVP stations.`, time: '1m ago', color: '#a78bfa' },
    { id: 2, agent: 'QA/QC Agent', msg: 'Sediment samples: 49/49 have valid lat/lon. Grain-size analysis complete for all stations. Classification breakdown: sand (18), silt (15), clay (10), gravel (6).', time: '3m ago', color: '#34d399' },
    { id: 3, agent: 'Anomaly Agent', msg: 'Anomaly A1 (paleo-channel) at 41.99°N: strong acoustic reflector in MCS data, 15-25m sub-bottom. Recommend additional chirp lines for delineation.', time: '8m ago', color: '#f87171' },
    { id: 4, agent: 'Anomaly Agent', msg: 'Anomaly A2 (gas-charged sediment): acoustic blanking in chirp near sample 2019-034-FA-002 (silty clay, 50% clay). Consistent with biogenic gas.', time: '12m ago', color: '#fbbf24' },
    { id: 5, agent: 'Support Agent', msg: 'CRS auto-detected: WGS84 (EPSG:4326) from shapefile .prj files. All layers in consistent projection.', time: '15m ago', color: '#00d4ff' },
  ];

  if (collapsed) {
    return (
      <div style={{ width: 40, background: C.bg2, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8 }}>
        <button onClick={() => setCollapsed(false)} title="Expand Field Agents"
          style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)', color: '#34d399', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          🤖
        </button>
        <span style={{ fontSize: 8, color: C.text3, marginTop: 4, writingMode: 'vertical-rl', textOrientation: 'mixed' }}>Agents</span>
      </div>
    );
  }

  return (
    <div style={{ width: 220, background: C.bg2, borderLeft: `1px solid ${C.border}`, overflow: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, color: C.text0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#34d399', display: 'inline-block' }} />
        Field Agents
        <button onClick={() => setCollapsed(true)} style={{ marginLeft: 'auto', fontSize: 14, background: 'none', border: 'none', color: C.text3, cursor: 'pointer', padding: '2px 4px' }} title="Collapse">›</button>
      </div>

      {/* Messages section — collapsible */}
      <button onClick={() => setExpandedAgents(v => !v)} style={{ padding: '6px 12px', fontSize: 10, fontWeight: 600, color: C.text2, background: 'transparent', border: 'none', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ transform: expandedAgents ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s', display: 'inline-block' }}>▸</span>
        Messages ({agentMsgs.length})
      </button>
      {expandedAgents && agentMsgs.length === 0 && (
        <div data-testid="agents-empty" style={{ flex: 1, padding: 12, fontSize: 10, color: C.text2, lineHeight: 1.5 }}>
          No agent activity for this project yet.
        </div>
      )}
      {expandedAgents && agentMsgs.length > 0 && (
        <div style={{ flex: 1, overflow: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {agentMsgs.map(m => (
            <div key={m.id} style={{ padding: '7px 9px', background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: m.color, marginBottom: 3 }}>{m.agent}</div>
              <div style={{ fontSize: 10, color: C.text1, lineHeight: 1.5, marginBottom: 3 }}>{m.msg}</div>
              <div style={{ fontSize: 9, color: C.text4 }}>{m.time}</div>
            </div>
          ))}
        </div>
      )}

      {/* Agent status */}
      {demoEnabled && <div style={{ padding: 8, borderTop: `1px solid ${C.border}` }}>
        {agents.map(a => (
          <div key={a.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 6px', borderRadius: 6, background: C.bg3, marginBottom: 3 }}>
            <span style={{ fontSize: 9, color: C.text2 }}>{a.label}</span>
            <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 99, background: a.status === 'Active' ? 'rgba(16,185,129,0.12)' : 'rgba(100,116,139,0.12)', color: a.status === 'Active' ? '#34d399' : '#8b949e', border: `1px solid ${a.status === 'Active' ? 'rgba(16,185,129,0.25)' : 'rgba(100,116,139,0.25)'}` }}>
              {a.status}
            </span>
          </div>
        ))}
      </div>}
    </div>
  );
}
